/**
 * New house ad (Admin → Ads, staff owners and admins): a campaign and its creative made by staff, without checkout
 * (worker/src/admarket.js createStaffCampaign, POST /api/dashboard/ads/market/new). The same order rules as the
 * advertisers' builder: sites, placement and format; the creative from an image uploaded here, an approved design from
 * the designer, or the house promo ("Your ad could be here"); the headline and link; targeting by page type, ward or
 * district, article and location (ZIP codes, cities, states); start and end dates; and a political ad's "Paid for by"
 * line and payer. It runs once saved: approved, no charge, marked as a house ad with who made it.
 */
import { useEffect, useMemo, useState } from "react";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { designPreview } from "../admarket/designPreview";
import { directAdHtml, addDays, type AdCampaign, type Creative } from "../../site/js/admarket.js";

const API = "/api/dashboard/ads/market";
interface Site { slug: string; siteName: string; entityType: string; districts: number; district: string; placements: string[] }
interface Options {
  sites: Site[]; placements: Record<string, { label: string; types: string[] }>; formats: Record<string, { label: string; sizes: string[]; required: string[]; units: string[]; kind: string }>;
  pageTypes: Record<string, string>; textLimits: Record<string, number>; limits: { minDays: number; maxDays: number };
  promo: { units: string[]; url: string }; designs: { id: string; name: string; board: string; size: string; political: boolean; paidForBy: string | null; advertiser: string }[]; today: string;
}
interface Upload { id: string; src: string; width: number; height: number; size: string }
type Source = "upload" | "design" | "promo";
const SIZE_LABEL: Record<string, string> = { native: "Card image (1200x628)", "728x90": "728x90", "468x60": "468x60 (optional)", "320x50": "320x50", "300x250": "300x250" };
const list = (s: string) => s.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return <label className="ads-new__field"><span className="ads-new__label">{label}</span>{children}{hint && <small className="muted">{hint}</small>}</label>;
}

export function NewHouseAd({ onCreated, onClose }: { onCreated: () => void; onClose: () => void }) {
  const [o, setO] = useState<Options | null>(null);
  const [name, setName] = useState("");
  const [sites, setSites] = useState<string[]>(["chicago"]);
  const [placement, setPlacement] = useState("article");
  const [format, setFormat] = useState("300x250");
  const [source, setSource] = useState<Source>("promo");
  const [images, setImages] = useState<Record<string, Upload>>({});
  const [designs, setDesigns] = useState<Record<string, string>>({});
  const [url, setUrl] = useState("");
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [alt, setAlt] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [districts, setDistricts] = useState("");
  const [articles, setArticles] = useState("");
  const [zips, setZips] = useState("");
  const [cities, setCities] = useState("");
  const [regions, setRegions] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pol, setPol] = useState({ is: false, kind: "candidate", paidForBy: "", payerName: "", payerAddress: "" });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    void fetch(`${API}/options`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((b: Options | null) => {
      if (!b) return;
      setO(b); setStart(b.today); setEnd(addDays(b.today, 29));
      setUrl(b.promo.url);
    }).catch(() => {});
  }, []);

  const fmt = o?.formats[format];
  const sizes = fmt?.sizes ?? [];
  const placements = useMemo(() => (o ? Object.keys(o.placements).filter((p) => sites.every((s) => o.sites.find((x) => x.slug === s)?.placements.includes(p))) : []), [o, sites]);
  useEffect(() => { if (o && !placements.includes(placement)) setPlacement(placements[0] ?? "sitewide"); }, [placements, placement, o]);
  useEffect(() => { setTypes([]); }, [placement]);
  if (!o) return <section className="approvals-recent ads-new" aria-label="New house ad"><p className="empty-panel">Loading…</p></section>;

  const unit = format === "leaderboard" ? "728x90" : format;
  const upload = async (size: string, file: File | undefined) => {
    if (!file) return;
    const res = await fetch(`${API}/uploads?size=${encodeURIComponent(size)}`, { method: "POST", credentials: "include", headers: { "content-type": file.type || "application/octet-stream" }, body: file }).catch(() => null);
    const b = res ? await res.json().catch(() => null) : null;
    if (res?.ok && b?.upload) setImages((x) => ({ ...x, [size]: b.upload }));
    else toastStore.getState().show({ message: errorText(b) ?? "Couldn't upload that image." });
  };
  const creative = (): Creative & { kind?: string; frame?: string } => {
    if (source === "promo") return { kind: "promo", frame: "/ads/promo?v=a", alt: "Your ad could be here", images: {} };
    const base = { headline, body, sponsor, alt, url, images: Object.fromEntries(Object.entries(images).map(([k, u]) => [k, { id: u.id, src: u.src, width: u.width, height: u.height }])) };
    return source === "design" ? designPreview({ ...base, designs: Object.fromEntries(Object.entries(designs).filter(([, v]) => v).map(([k, v]) => [k, { id: v }])) }, unit, "/api/dashboard/ads/designs") : base;
  };
  const ad: AdCampaign = { id: "c_preview", placement, format, political: pol.is, paidForBy: pol.paidForBy, creatives: {} };
  const preview = directAdHtml(ad, creative(), { unit, href: "#" });

  const submit = async () => {
    setBusy(true); setErrors([]);
    const order = {
      name, sites, placement, format, start, end,
      targeting: { types, districts: list(districts).map(Number), articles: list(articles), geo: { zips, cities: list(cities), regions: list(regions) } },
      political: pol.is ? pol : null,
      creatives: { a: source === "promo" ? { promo: true, url } : {
        url, headline, body, sponsor, alt,
        images: source === "upload" ? Object.fromEntries(Object.entries(images).map(([k, u]) => [k, u.id])) : {},
        designs: source === "design" ? Object.fromEntries(Object.entries(designs).filter(([, v]) => v)) : {},
      } },
    };
    const res = await fetch(`${API}/new`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(order) }).catch(() => null);
    const b = res ? await res.json().catch(() => null) : null;
    setBusy(false);
    if (res?.status === 201) { toastStore.getState().show({ message: `${b.campaign.name}: live from ${b.campaign.start}` }); onCreated(); return; }
    setErrors(b?.errors ?? [errorText(b) ?? "Couldn't create it. Try again."]);
  };

  const maxDistrict = Math.max(0, ...sites.map((s) => o.sites.find((x) => x.slug === s)?.districts ?? 0));
  return (
    <section className="approvals-recent ads-new" aria-labelledby="ads-new-title">
      <div className="ads-new__head">
        <h2 className="section-title" id="ads-new-title">New house ad</h2>
        <button className="btn btn--small" type="button" onClick={onClose}>Close</button>
      </div>
      <p className="muted">Runs once saved: approved, no charge, marked as a house ad made by you. Paid campaigns that fit a slot still come first.</p>
      <div className="ads-new__grid">
        <form className="ads-new__form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <fieldset className="ads-new__set"><legend>Campaign</legend>
            <Field label="Name"><input className="tset-pill" value={name} maxLength={o.textLimits.name} onChange={(e) => setName(e.target.value)} placeholder="Fall voter guide promo" required /></Field>
            <div className="ads-new__field"><span className="ads-new__label">Sites</span>
              <div className="ads-new__chips">{o.sites.map((s) => (
                <label key={s.slug} className="ads-new__chip"><input type="checkbox" checked={sites.includes(s.slug)} onChange={(e) => setSites(e.target.checked ? [...sites, s.slug] : sites.filter((x) => x !== s.slug))} />{s.siteName}</label>
              ))}</div>
            </div>
            <div className="ads-new__row">
              <Field label="Placement"><select className="tset-pill" value={placement} onChange={(e) => setPlacement(e.target.value)}>{placements.map((p) => <option key={p} value={p}>{o.placements[p].label}</option>)}</select></Field>
              <Field label="Format"><select className="tset-pill" value={format} onChange={(e) => { setFormat(e.target.value); setImages({}); setDesigns({}); }}>{Object.entries(o.formats).map(([id, f]) => <option key={id} value={id}>{f.label}</option>)}</select></Field>
            </div>
            <div className="ads-new__row">
              <Field label="Start"><input className="tset-pill" type="date" value={start} min={o.today} onChange={(e) => setStart(e.target.value)} required /></Field>
              <Field label="End"><input className="tset-pill" type="date" value={end} min={start} max={start ? addDays(start, o.limits.maxDays - 1) : undefined} onChange={(e) => setEnd(e.target.value)} required /></Field>
            </div>
          </fieldset>

          <fieldset className="ads-new__set"><legend>Creative</legend>
            <div className="seg" role="radiogroup" aria-label="Creative">
              {([["promo", "House promo"], ["upload", "Upload images"], ["design", "Designer creative"]] as const).map(([id, l]) => <button key={id} type="button" role="radio" aria-checked={source === id} onClick={() => setSource(id)}>{l}</button>)}
            </div>
            {source === "promo" && <p className="muted art-small">“Reach [City] voters”, named for each site, linking to the sign-up page on citypolitics.org with the site and slot in the link.</p>}
            {source === "upload" && sizes.map((size) => (
              <Field key={size} label={SIZE_LABEL[size] ?? size} hint={images[size] ? `${images[size].width}x${images[size].height} uploaded` : fmt?.required.includes(size) ? "Required: that size, or twice it for sharp screens." : undefined}>
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(e) => void upload(size, e.target.files?.[0])} aria-label={`Image for ${size}`} />
              </Field>
            ))}
            {source === "design" && sizes.map((size) => {
              const opts = o.designs.filter((d) => d.size === size);
              return (
                <Field key={size} label={SIZE_LABEL[size] ?? size} hint={!opts.length ? "No approved designs in this size yet." : undefined}>
                  <select className="tset-pill" value={designs[size] ?? ""} onChange={(e) => setDesigns({ ...designs, [size]: e.target.value })}>
                    <option value="">{fmt?.required.includes(size) ? "Pick a design…" : "None"}</option>
                    {opts.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.board}{d.advertiser ? ` · ${d.advertiser}` : ""}{d.political ? " · political" : ""}</option>)}
                  </select>
                </Field>
              );
            })}
            <Field label="Link" hint="A full https:// address."><input className="tset-pill" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" required /></Field>
            {source !== "promo" && <>
              <Field label="Headline"><input className="tset-pill" value={headline} maxLength={o.textLimits.headline} onChange={(e) => setHeadline(e.target.value)} /></Field>
              {format === "native" && <>
                <Field label="Line of text"><input className="tset-pill" value={body} maxLength={o.textLimits.body} onChange={(e) => setBody(e.target.value)} /></Field>
                <Field label="Advertiser name on the card"><input className="tset-pill" value={sponsor} maxLength={o.textLimits.sponsor} onChange={(e) => setSponsor(e.target.value)} /></Field>
              </>}
              <Field label="Alt text" hint="What the image says, for screen readers."><input className="tset-pill" value={alt} maxLength={120} onChange={(e) => setAlt(e.target.value)} /></Field>
            </>}
          </fieldset>

          <fieldset className="ads-new__set"><legend>Targeting</legend>
            <div className="ads-new__chips">{(o.placements[placement]?.types ?? []).filter((t) => t !== "partner").map((t) => (
              <label key={t} className="ads-new__chip"><input type="checkbox" checked={types.includes(t)} onChange={(e) => setTypes(e.target.checked ? [...types, t] : types.filter((x) => x !== t))} />{o.pageTypes[t] ?? t}</label>
            ))}</div>
            <small className="muted">No page type picked: the whole placement.</small>
            {maxDistrict > 0 && <Field label="Wards or districts" hint={`Numbers 1 to ${maxDistrict}, separated by commas.`}><input className="tset-pill" value={districts} onChange={(e) => setDistricts(e.target.value)} placeholder="1, 2, 27" /></Field>}
            {(placement === "article" || placement === "sitewide") && <Field label="Articles" hint="On My Block article slugs, separated by commas."><input className="tset-pill" value={articles} onChange={(e) => setArticles(e.target.value)} /></Field>}
            <Field label="ZIP codes" hint="Optional. Readers whose location is in one of these, approximately."><textarea className="tset-pill ads-new__area" value={zips} onChange={(e) => setZips(e.target.value)} placeholder="60614, 60657" /></Field>
            <div className="ads-new__row">
              <Field label="Cities"><input className="tset-pill" value={cities} onChange={(e) => setCities(e.target.value)} placeholder="Optional" /></Field>
              <Field label="States"><input className="tset-pill" value={regions} onChange={(e) => setRegions(e.target.value)} placeholder="IL, PA" /></Field>
            </div>
          </fieldset>

          <fieldset className="ads-new__set"><legend>Political</legend>
            <label className="ads-new__chip"><input type="checkbox" checked={pol.is} onChange={(e) => setPol({ ...pol, is: e.target.checked })} />This is a political ad</label>
            {pol.is && <>
              <Field label="Kind"><select className="tset-pill" value={pol.kind} onChange={(e) => setPol({ ...pol, kind: e.target.value })}><option value="candidate">Candidate</option><option value="pac">PAC</option><option value="ballot">Ballot measure</option><option value="issue">Issue</option></select></Field>
              <Field label="Paid for by" hint="Shown on the ad."><input className="tset-pill" value={pol.paidForBy} maxLength={o.textLimits.paidForBy} onChange={(e) => setPol({ ...pol, paidForBy: e.target.value })} /></Field>
              <Field label="Payer's name"><input className="tset-pill" value={pol.payerName} maxLength={o.textLimits.payerName} onChange={(e) => setPol({ ...pol, payerName: e.target.value })} /></Field>
              <Field label="Payer's address"><input className="tset-pill" value={pol.payerAddress} maxLength={o.textLimits.payerAddress} onChange={(e) => setPol({ ...pol, payerAddress: e.target.value })} /></Field>
            </>}
          </fieldset>

          {errors.length > 0 && <ul className="ads-new__errors" role="alert">{errors.map((x) => <li key={x}>{x}</li>)}</ul>}
          <div className="ads-review__actions">
            <button className="btn btn--primary" type="submit" disabled={busy || !sites.length || !name.trim()}>{busy ? "Creating…" : "Create and run"}</button>
            <button className="btn btn--small" type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
        <aside className="ads-new__preview" aria-label="Preview">
          <p className="ads-new__label">Preview · {unit}</p>
          <div className="ads-creative"><div className="ads-slot"><p>Advertisement</p>
            {preview ? <div dangerouslySetInnerHTML={{ __html: preview }} /> : <p className="muted art-small">Add the {source === "design" ? "designs" : "images"} to see it.</p>}
          </div></div>
        </aside>
      </div>
    </section>
  );
}
